import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import AppointmentsList from '../../components/schedule/AppointmentsList';
import { HourlyAvailabilityGrid, DateAvailability } from '../../components/schedule/HourlyAvailabilityGrid';
import { scheduleService } from '../../services/schedule.service';
import { CalendarService, UserCalendarSettings, CalendarConnection } from '../../services/calendar.service';
import { Schedule, TimeSlot } from '../../types/schedule.types';
import { LinkIcon, CloudIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { Calendar, RefreshCw, Check, X, Settings, ExternalLink, Users } from 'lucide-react';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { projectService } from '../../services/project.service';
import { getUserById } from '../../services/user.service';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useProjectSegments } from '../../hooks/useProjectSegments';
import { SchoolSegmentSelect } from '../../components/leads/SchoolSegmentSelect';

interface ClientUser {
  userId: string;
  name: string;
  email: string;
  role: string;
}

export const SchedulePage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { selectedProjectId, selectedProject } = useProject();
  const { segments } = useProjectSegments(selectedProjectId);
  // Which campus's availability is being edited (multi-school projects). The
  // user holds one schedule doc per campus, plus an untagged project-wide one.
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | undefined>(undefined);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);

  // Date-based availability state
  const [myAvailability, setMyAvailability] = useState<DateAvailability | null>(null);
  const [savingAvailability, setSavingAvailability] = useState(false);
  // Max parents that can book the same time slot. Defaults to 1.
  const [mySlotCapacity, setMySlotCapacity] = useState<number>(1);
  const [savingCapacity, setSavingCapacity] = useState(false);

  // Persist defaultSlotCapacity independently of availability so a capacity-
  // only edit doesn't get stranded by the grid's hasChanges flag.
  const persistMySlotCapacity = async (newCapacity: number) => {
    if (!currentUser || !selectedProjectId || !userProfile) return;
    setSavingCapacity(true);
    try {
      // Find or create the schedule (scoped to the selected campus), then patch
      // the capacity field.
      const seg = selectedSegmentId ?? null;
      let mySchedule = (await scheduleService.getSchedulesByProject(selectedProjectId))
        .find(s => s.userId === currentUser.uid && (s.schoolSegmentId ?? null) === seg);

      if (!mySchedule) {
        // No schedule yet — create a stub one carrying the capacity.
        mySchedule = await scheduleService.createSchedule({
          userId: currentUser.uid,
          userName: userProfile.name || userProfile.email || 'User',
          projectId: selectedProjectId,
          defaultSlotCapacity: newCapacity,
          isActive: true,
          ...(selectedSegmentId ? { schoolSegmentId: selectedSegmentId } : {}),
        });
      } else if (mySchedule.id) {
        await scheduleService.updateSchedule(mySchedule.id, {
          defaultSlotCapacity: newCapacity,
        });
      }

      toast.success('Slot capacity saved');
    } catch (error) {
      console.error('Error saving slot capacity:', error);
      toast.error('Failed to save slot capacity');
    } finally {
      setSavingCapacity(false);
    }
  };

  // Client user's schedule (for other users to view)
  const [clientUserSchedule, setClientUserSchedule] = useState<Schedule | null>(null);

  // Provider editing client schedule state
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedClientAvailability, setSelectedClientAvailability] = useState<DateAvailability | null>(null);
  const [savingClientAvailability, setSavingClientAvailability] = useState(false);

  // Form states for new time slot (legacy - kept for backward compatibility)
  const [newSlot, setNewSlot] = useState<Partial<TimeSlot>>({
    dayOfWeek: 1,
    startTime: '09:00',
    endTime: '17:00',
    maxAppointments: 10
  });

  // Calendar integration state
  const [calendarSettings, setCalendarSettings] = useState<UserCalendarSettings | null>(null);
  const [showCalendarSettings, setShowCalendarSettings] = useState(false);
  const [connectingCalendar, setConnectingCalendar] = useState<string | null>(null);
  const [iCloudCredentials, setICloudCredentials] = useState({ appleId: '', appPassword: '' });
  const [showICloudModal, setShowICloudModal] = useState(false);

  // Check if current user is a provider who can edit client schedules
  const isProvider = userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user';

  useEffect(() => {
    if (selectedProjectId && currentUser) {
      loadSchedules();
      if (isProvider) {
        loadClientUsers();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, currentUser, selectedSegmentId]);

  // Load client users for the project (for provider to select who to edit)
  const loadClientUsers = async () => {
    if (!selectedProjectId) return;

    try {
      // Query users who have this project in their projects array
      // This is more reliable than project.assignedUsers which may be out of sync
      const usersQuery = query(
        collection(db, 'users'),
        where('projects', 'array-contains', selectedProjectId)
      );
      const usersSnapshot = await getDocs(usersQuery);

      const clients: ClientUser[] = [];
      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        // Filter for client roles
        if (userData.role === 'client_user' || userData.role === 'client_admin') {
          clients.push({
            userId: doc.id,
            name: userData.name || userData.email || 'Unknown',
            email: userData.email || '',
            role: userData.role
          });
        }
      });

      setClientUsers(clients);

      // If there's only one client, auto-select them
      if (clients.length === 1) {
        handleSelectClient(clients[0].userId);
      }
    } catch (error) {
      console.error('Error loading client users:', error);
    }
  };

  // When the campus changes, re-derive the selected client's displayed
  // availability for that campus.
  useEffect(() => {
    if (isProvider && selectedClientId) {
      handleSelectClient(selectedClientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSegmentId]);

  // Handle selecting a client to edit their schedule
  const handleSelectClient = async (clientUserId: string) => {
    setSelectedClientId(clientUserId);

    if (!clientUserId || !selectedProjectId) {
      setSelectedClientAvailability(null);
      return;
    }

    // Find the client's schedule (scoped to the selected campus)
    const seg = selectedSegmentId ?? null;
    const clientSchedule = schedules.find(s => s.userId === clientUserId && (s.schoolSegmentId ?? null) === seg);
    if (clientSchedule) {
      if (clientSchedule.dateAvailability) {
        setSelectedClientAvailability(clientSchedule.dateAvailability);
      } else if (clientSchedule.hourlyAvailability) {
        setSelectedClientAvailability(clientSchedule.hourlyAvailability as any);
      } else {
        setSelectedClientAvailability(null);
      }
    } else {
      setSelectedClientAvailability(null);
    }
  };

  // Save availability for a selected client (used by providers)
  const handleSaveClientAvailability = async (availability: DateAvailability) => {
    if (!selectedClientId || !selectedProjectId) return;

    const selectedClient = clientUsers.find(c => c.userId === selectedClientId);
    if (!selectedClient) return;

    setSavingClientAvailability(true);
    try {
      await scheduleService.saveDateAvailability(
        selectedClientId,
        selectedProjectId,
        selectedClient.name,
        availability,
        selectedSegmentId
      );
      setSelectedClientAvailability(availability);
      toast.success(`Schedule updated for ${selectedClient.name}`);
      // Reload schedules to reflect changes
      await loadSchedules();
    } catch (error) {
      console.error('Error saving client availability:', error);
      toast.error('Failed to save schedule');
      throw error;
    } finally {
      setSavingClientAvailability(false);
    }
  };

  const loadSchedules = async () => {
    if (!selectedProjectId || !currentUser) return;

    setLoading(true);
    try {
      const projectSchedules = await scheduleService.getSchedulesByProject(selectedProjectId);
      setSchedules(projectSchedules);

      // Load current user's availability (for client_user to edit), scoped to
      // the selected campus.
      const mySeg = selectedSegmentId ?? null;
      const mySchedule = projectSchedules.find(s => s.userId === currentUser.uid && (s.schoolSegmentId ?? null) === mySeg);
      if (mySchedule) {
        // Use date-based availability if exists
        if (mySchedule.dateAvailability) {
          setMyAvailability(mySchedule.dateAvailability);
        } else if (mySchedule.hourlyAvailability) {
          // Legacy weekly format - will be converted by the grid component
          setMyAvailability(mySchedule.hourlyAvailability as any);
        } else {
          setMyAvailability(null);
        }
        setMySlotCapacity(mySchedule.defaultSlotCapacity ?? 1);
      } else {
        setMyAvailability(null);
        setMySlotCapacity(1);
      }

      // Find client_user's schedule for other users to view
      // We need to look up user roles - for now, find schedules and the project should have client info
      // The client_user schedule is typically the one that's not the current user's if they're not client_user
      if (userProfile?.role !== 'client_user') {
        // Find a schedule that belongs to a client_user
        // Since we don't have role info in schedule, we look for any schedule that's not ours
        // In a real app, you'd query users to find client_user's schedule
        const otherSchedules = projectSchedules.filter(s => s.userId !== currentUser.uid);
        if (otherSchedules.length > 0) {
          // Use the first one (typically the client_user's)
          setClientUserSchedule(otherSchedules[0]);
        } else {
          setClientUserSchedule(null);
        }
      } else {
        // If current user is client_user, they see their own schedule (already loaded above)
        setClientUserSchedule(null);
      }
    } catch (error) {
      console.error('Error loading schedules:', error);
      toast.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAvailability = async (availability: DateAvailability) => {
    if (!currentUser || !selectedProjectId || !userProfile) return;

    setSavingAvailability(true);
    try {
      await scheduleService.saveDateAvailability(
        currentUser.uid,
        selectedProjectId,
        userProfile.name || userProfile.email || 'User',
        availability,
        selectedSegmentId
      );
      setMyAvailability(availability);
      // Reload schedules to reflect changes
      await loadSchedules();
    } catch (error) {
      console.error('Error saving availability:', error);
      throw error;
    } finally {
      setSavingAvailability(false);
    }
  };

  const handleAddTimeSlot = async () => {
    if (!currentUser || !selectedProjectId) return;

    try {
      // Find or create schedule for the user
      let schedule = schedules.find(s => s.userId === currentUser.uid);

      if (!schedule) {
        // Create new schedule
        schedule = await scheduleService.createSchedule({
          userId: currentUser.uid,
          userName: userProfile?.name || '',
          projectId: selectedProjectId,
          timeSlots: [],
          isActive: true
        });
      }

      // Add time slot
      const updatedSlots = [...(schedule.timeSlots || []), newSlot as TimeSlot];
      await scheduleService.updateSchedule(schedule.id!, { timeSlots: updatedSlots });

      toast.success('Time slot added successfully');
      loadSchedules();
    } catch (error) {
      console.error('Error adding time slot:', error);
      toast.error('Failed to add time slot');
    }
  };

  const handleDeleteTimeSlot = async (scheduleId: string, slotIndex: number) => {
    try {
      const schedule = schedules.find(s => s.id === scheduleId);
      if (!schedule) return;

      const updatedSlots = (schedule.timeSlots || []).filter((_, index) => index !== slotIndex);
      await scheduleService.updateSchedule(scheduleId, { timeSlots: updatedSlots });

      toast.success('Time slot removed');
      loadSchedules();
    } catch (error) {
      console.error('Error removing time slot:', error);
      toast.error('Failed to remove time slot');
    }
  };

  const getDayName = (dayOfWeek: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek];
  };

  // Load calendar settings
  const loadCalendarSettings = async () => {
    if (!currentUser) return;
    try {
      const settings = await CalendarService.getUserSettings(currentUser.uid);
      setCalendarSettings(settings);
    } catch (error) {
      console.error('Error loading calendar settings:', error);
    }
  };

  useEffect(() => {
    if (currentUser) {
      loadCalendarSettings();
    }
  }, [currentUser]);

  // Connect Google Calendar
  const handleConnectGoogle = async () => {
    if (!currentUser) return;
    setConnectingCalendar('google');
    try {
      const authUrl = await CalendarService.connectGoogleCalendar(currentUser.uid);
      // In production, redirect to OAuth URL
      // window.location.href = authUrl;
      toast.success('Google Calendar connection initiated. OAuth flow would redirect here.');
      // For demo, simulate successful connection
      setTimeout(() => {
        loadCalendarSettings();
        setConnectingCalendar(null);
        toast.success('Google Calendar connected!');
      }, 1500);
    } catch (error: any) {
      console.error('Error connecting Google Calendar:', error);
      toast.error(error.message || 'Failed to connect Google Calendar');
      setConnectingCalendar(null);
    }
  };

  // Connect Outlook Calendar
  const handleConnectOutlook = async () => {
    if (!currentUser) return;
    setConnectingCalendar('outlook');
    try {
      const authUrl = await CalendarService.connectOutlookCalendar(currentUser.uid);
      // In production, redirect to OAuth URL
      // window.location.href = authUrl;
      toast.success('Outlook Calendar connection initiated. OAuth flow would redirect here.');
      setTimeout(() => {
        loadCalendarSettings();
        setConnectingCalendar(null);
        toast.success('Outlook Calendar connected!');
      }, 1500);
    } catch (error: any) {
      console.error('Error connecting Outlook Calendar:', error);
      toast.error(error.message || 'Failed to connect Outlook Calendar');
      setConnectingCalendar(null);
    }
  };

  // Connect iCloud Calendar
  const handleConnectICloud = async () => {
    if (!currentUser || !iCloudCredentials.appleId || !iCloudCredentials.appPassword) {
      toast.error('Please enter your Apple ID and app-specific password');
      return;
    }
    setConnectingCalendar('icloud');
    try {
      await CalendarService.connectICloudCalendar(
        currentUser.uid,
        iCloudCredentials.appleId,
        iCloudCredentials.appPassword
      );
      toast.success('iCloud Calendar connected!');
      setShowICloudModal(false);
      setICloudCredentials({ appleId: '', appPassword: '' });
      loadCalendarSettings();
    } catch (error: any) {
      console.error('Error connecting iCloud Calendar:', error);
      toast.error(error.message || 'Failed to connect iCloud Calendar');
    } finally {
      setConnectingCalendar(null);
    }
  };

  // Disconnect calendar
  const handleDisconnectCalendar = async (provider: 'google' | 'outlook' | 'icloud') => {
    if (!currentUser) return;
    try {
      await CalendarService.disconnectCalendar(currentUser.uid, provider as any);
      toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} Calendar disconnected`);
      loadCalendarSettings();
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      toast.error('Failed to disconnect calendar');
    }
  };

  // Get calendar connection status
  const getCalendarConnection = (provider: string): CalendarConnection | undefined => {
    return calendarSettings?.connections.find(c => c.provider === provider);
  };

  // Check if user can manage calendar (client_user, client_admin, provider_admin)
  const canManageCalendar = userProfile?.role === 'client_user' ||
                            userProfile?.role === 'client_admin' ||
                            userProfile?.role === 'provider_admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedule</h1>
          <p className="text-gray-600 mt-1">
            Manage your availability and appointments
            {selectedProject && <span className="text-indigo-600"> - {selectedProject.name}</span>}
          </p>
        </div>
      </div>

      {/* My Availability - Hourly Grid (for client_user to edit) */}
      {userProfile?.role === 'client_user' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">My Availability</h2>
          <p className="text-sm text-gray-600 mb-4">
            Click on time slots to mark when you're available for appointments. Slots are 30 minutes each. Toggle between days using the tabs above.
          </p>
          {segments.length > 0 && (
            <div className="mb-4 max-w-xs">
              <SchoolSegmentSelect
                segments={segments}
                value={selectedSegmentId}
                onChange={setSelectedSegmentId}
                label="Availability for school / campus"
                emptyLabel="All schools (project-wide)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Set separate availability per campus. Parents booking for that school see only these times.
              </p>
            </div>
          )}
          <div className="mb-4 flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700" htmlFor="slot-capacity">
              Max parents per time slot
            </label>
            <input
              id="slot-capacity"
              type="number"
              min={1}
              max={20}
              value={mySlotCapacity}
              onChange={(e) => setMySlotCapacity(Math.max(1, parseInt(e.target.value, 10) || 1))}
              onBlur={(e) => {
                const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                persistMySlotCapacity(val);
              }}
              disabled={savingCapacity}
              className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
            />
            <span className="text-xs text-gray-500">
              {savingCapacity ? 'Saving…' : 'Saved automatically when you click away.'}
            </span>
          </div>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <LoadingSpinner />
            </div>
          ) : selectedProjectId ? (
            <HourlyAvailabilityGrid
              key={`my-${selectedSegmentId || 'all'}`}
              initialAvailability={myAvailability || undefined}
              onSave={handleSaveAvailability}
              saving={savingAvailability}
            />
          ) : (
            <p className="text-gray-500">Select a project to manage availability</p>
          )}
        </div>
      )}

      {/* Client User's Availability - Editable for providers, read-only for others */}
      {userProfile?.role !== 'client_user' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-600" />
              <h2 className="text-lg font-medium text-gray-900">Client Availability</h2>
            </div>
          </div>

          {/* Provider can select and edit client schedules */}
          {isProvider && clientUsers.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Client to Edit Schedule
              </label>
              <select
                value={selectedClientId}
                onChange={(e) => handleSelectClient(e.target.value)}
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">-- Select a client --</option>
                {clientUsers.map((client) => (
                  <option key={client.userId} value={client.userId}>
                    {client.name} ({client.role === 'client_admin' ? 'Admin' : 'User'})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                As a provider, you can update client schedules on their behalf.
              </p>
            </div>
          )}

          {/* Campus selector — scope the availability being viewed/edited */}
          {segments.length > 0 && (
            <div className="mb-4 max-w-xs">
              <SchoolSegmentSelect
                segments={segments}
                value={selectedSegmentId}
                onChange={setSelectedSegmentId}
                label="School / Campus"
                emptyLabel="All schools (project-wide)"
              />
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-32">
              <LoadingSpinner />
            </div>
          ) : isProvider && selectedClientId ? (
            // Provider editing selected client's schedule
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Editing schedule for: <strong>{clientUsers.find(c => c.userId === selectedClientId)?.name}</strong>
              </p>
              <HourlyAvailabilityGrid
                key={`client-${selectedClientId}-${selectedSegmentId || 'all'}`}
                initialAvailability={selectedClientAvailability || undefined}
                onSave={handleSaveClientAvailability}
                saving={savingClientAvailability}
              />
            </div>
          ) : isProvider && clientUsers.length > 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Select a client above to view or edit their schedule.</p>
            </div>
          ) : isProvider && clientUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No client users assigned to this project yet.</p>
            </div>
          ) : clientUserSchedule ? (
            // Non-provider read-only view
            <HourlyAvailabilityGrid
              initialAvailability={
                clientUserSchedule.dateAvailability ||
                clientUserSchedule.hourlyAvailability as any ||
                undefined
              }
              readOnly={true}
              userName={clientUserSchedule.userName}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No client availability set for this project yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Upcoming Appointments */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Upcoming Appointments</h2>
        <AppointmentsList
          projectId={selectedProjectId || ''}
          limit={5}
          showActions={true}
        />
      </div>

      {/* Calendar Integration - Only for client_user, client_admin, provider_admin */}
      {canManageCalendar && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-indigo-600" />
              <h2 className="text-lg font-medium text-gray-900">Calendar Sync</h2>
            </div>
            <button
              onClick={() => setShowCalendarSettings(!showCalendarSettings)}
              className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
            >
              <Settings className="h-4 w-4" />
              {showCalendarSettings ? 'Hide Settings' : 'Show Settings'}
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Connect your calendar to automatically sync tours and calls. Appointments will appear in your personal calendar.
          </p>

          {/* Calendar Connection Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Google Calendar */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                  <span className="font-medium">Google</span>
                </div>
                {getCalendarConnection('google')?.connected ? (
                  <Check className="h-5 w-5 text-green-500" />
                ) : null}
              </div>
              {getCalendarConnection('google')?.connected ? (
                <div className="space-y-2">
                  <p className="text-xs text-green-600">Connected</p>
                  <button
                    onClick={() => handleDisconnectCalendar('google')}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Disconnect
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleConnectGoogle}
                  disabled={connectingCalendar === 'google'}
                  className="w-full py-2 px-3 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {connectingCalendar === 'google' ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <LinkIcon className="h-4 w-4" />
                  )}
                  Connect
                </button>
              )}
            </div>

            {/* Outlook Calendar */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5">
                      <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.583-.16.159-.354.238-.584.238h-8.547v-6.036l1.203.926c.114.08.252.12.415.12.163 0 .301-.04.415-.12l6.336-4.879V7.387zM8.547 12.65v6.036H.822c-.23 0-.424-.079-.583-.238A.792.792 0 010 17.865V7.387l8.547 5.263zm6.82-5.263v3.693l-6.82 5.25-6.82-5.25V7.387c0-.23.08-.424.238-.583.159-.159.354-.238.584-.238h12.033c.23 0 .424.08.584.238.158.16.238.354.238.584l-.038-.001z"/>
                    </svg>
                  </div>
                  <span className="font-medium">Outlook</span>
                </div>
                {getCalendarConnection('outlook')?.connected ? (
                  <Check className="h-5 w-5 text-green-500" />
                ) : null}
              </div>
              {getCalendarConnection('outlook')?.connected ? (
                <div className="space-y-2">
                  <p className="text-xs text-green-600">Connected</p>
                  <button
                    onClick={() => handleDisconnectCalendar('outlook')}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Disconnect
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleConnectOutlook}
                  disabled={connectingCalendar === 'outlook'}
                  className="w-full py-2 px-3 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {connectingCalendar === 'outlook' ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <LinkIcon className="h-4 w-4" />
                  )}
                  Connect
                </button>
              )}
            </div>

            {/* iCloud Calendar */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <CloudIcon className="h-5 w-5 text-gray-600" />
                  </div>
                  <span className="font-medium">iCloud</span>
                </div>
                {getCalendarConnection('icloud')?.connected ? (
                  <Check className="h-5 w-5 text-green-500" />
                ) : null}
              </div>
              {getCalendarConnection('icloud')?.connected ? (
                <div className="space-y-2">
                  <p className="text-xs text-green-600">Connected</p>
                  <button
                    onClick={() => handleDisconnectCalendar('icloud')}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Disconnect
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowICloudModal(true)}
                  disabled={connectingCalendar === 'icloud'}
                  className="w-full py-2 px-3 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {connectingCalendar === 'icloud' ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <LinkIcon className="h-4 w-4" />
                  )}
                  Connect
                </button>
              )}
            </div>
          </div>

          {/* Sync Settings (expandable) */}
          {showCalendarSettings && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Sync Settings</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input type="checkbox" defaultChecked className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  <span className="text-sm text-gray-700">Sync tour appointments</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" defaultChecked className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  <span className="text-sm text-gray-700">Sync follow-up calls</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" defaultChecked className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  <span className="text-sm text-gray-700">Include parent contact info in event</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" defaultChecked className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  <span className="text-sm text-gray-700">Add 15 minute reminder</span>
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {/* iCloud Connection Modal */}
      {showICloudModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Connect iCloud Calendar</h3>
              <button onClick={() => setShowICloudModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              To connect iCloud Calendar, you'll need an app-specific password.{' '}
              <a
                href="https://support.apple.com/en-us/HT204397"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
              >
                Learn how to create one <ExternalLink className="h-3 w-3" />
              </a>
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apple ID</label>
                <input
                  type="email"
                  value={iCloudCredentials.appleId}
                  onChange={(e) => setICloudCredentials({ ...iCloudCredentials, appleId: e.target.value })}
                  placeholder="your@icloud.com"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">App-Specific Password</label>
                <input
                  type="password"
                  value={iCloudCredentials.appPassword}
                  onChange={(e) => setICloudCredentials({ ...iCloudCredentials, appPassword: e.target.value })}
                  placeholder="xxxx-xxxx-xxxx-xxxx"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowICloudModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConnectICloud}
                disabled={connectingCalendar === 'icloud'}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {connectingCalendar === 'icloud' && <RefreshCw className="h-4 w-4 animate-spin" />}
                Connect
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};